// Adaptador USB-serie FT232RL en 3D: módulo azul con Mini-USB, chip SSOP-28, puente de 5 V / 3.3 V, LED de TX y RX
// y el encabezado de 6 pines. El origen está en el centro del encabezado y la placa se extiende hacia −Z.
import * as THREE from 'three';
import { Builder, M, mat, canvas, texture, text, extrudeBoard, boardShape } from '../kit.js';
import { FTDI } from '../../parts/usbserial.js';
import { getPins } from '../../parts/registry.js';
import { P, smdLed, smd, jumperCap, labelPlane, cable } from './actuators-common.js';

const W = FTDI.W;
const D = FTDI.D;
const TOP = FTDI.PCB_TOP;
const ZC = -(D / 2 - 1.27); // centro de la placa: el encabezado queda a 1.27 mm del borde
const BLUE = '#1857b5';

let silkTex = null;

function drawSilk() {
  const { c, g } = canvas(W, D, 24);
  const X = (x) => x + W / 2;
  const Y = (z) => z - ZC + D / 2;
  g.fillStyle = BLUE;
  g.fillRect(0, 0, W, D);
  g.strokeStyle = 'rgba(8,36,90,0.55)';
  g.lineWidth = 0.22;
  for (let i = 0; i < 6; i++) {
    g.beginPath();
    g.moveTo(X((i - 2.5) * P), Y(-1));
    g.lineTo(X((i - 2.5) * P), Y(-10 - i * 0.7));
    g.lineTo(X((i - 2.5) * P * 0.7), Y(-14));
    g.stroke();
  }
  const white = '#f3f6fb';
  for (let i = 0; i < 6; i++) {
    g.fillStyle = '#d8b465';
    g.beginPath();
    g.arc(X((i - 2.5) * P), Y(0), 0.95, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#121214';
    g.beginPath();
    g.arc(X((i - 2.5) * P), Y(0), 0.45, 0, Math.PI * 2);
    g.fill();
  }
  ['GND', 'CTS', 'VCC', 'TXD', 'RXD', 'DTR'].forEach((name, i) => text(g, name, X((i - 2.5) * P), Y(-2.4), { size: 0.95, color: white, align: 'right', rotate: -Math.PI / 2, weight: 700 }));
  text(g, 'FT232RL', X(0), Y(-26.2), { size: 1.7, color: white, weight: 800 });
  text(g, 'USB ⇄ TTL', X(0), Y(-23.6), { size: 1.25, color: white, weight: 700 });
  text(g, '5V', X(-6.4), Y(-12.6), { size: 1.0, color: white, weight: 700 });
  text(g, '3V3', X(-6.4), Y(-17.7), { size: 1.0, color: white, weight: 700 });
  text(g, 'PWR', X(5.8), Y(-9.4), { size: 0.8, color: white, weight: 700 });
  text(g, 'TX', X(5.8), Y(-12.0), { size: 0.8, color: white, weight: 700 });
  text(g, 'RX', X(5.8), Y(-14.6), { size: 0.8, color: white, weight: 700 });
  const t = texture(c);
  t.userData.shared = true;
  return t;
}

export function buildUsbSerial(def, props) {
  if (!silkTex) silkTex = drawSilk();
  const pins = getPins(def.type, props);
  const b = new Builder();
  const pcb = extrudeBoard(boardShape(W, D, { r: 0.6 }), FTDI.PCB_T, silkTex, '#103b80', { w: W, d: D, y0: FTDI.PCB_Y });
  pcb.position.z = ZC;
  b.root.add(pcb);
  // Encabezado: separador negro y patas doradas hacia abajo (a la protoboard)
  b.box(6 * P, 2.5, P, M.blackPlastic, 0, 0.1, 0);
  for (const p of pins) {
    b.box(0.64, TOP + 0.9, 0.64, M.gold, p.x, 0, p.z);
    b.cyl(0.85, 0.85, 0.3, M.tin, p.x, TOP, p.z, 10);
  }
  // Conector Mini-USB: carcasa metálica que sobresale del borde y su boca
  const usbZ = ZC - D / 2 - 0.2 + 4.0;
  b.box(7.7, 3.9, 8.4, M.metal, 0, TOP, usbZ);
  b.box(5.6, 1.4, 0.3, M.hole, 0, TOP + 1.2, usbZ - 4.25);
  // FT232RL (SSOP-28: 10.2 × 5.3 mm) con patillas a los dos lados
  b.box(10.2, 1.9, 5.3, M.chip, 0, TOP, -20.6);
  for (const s of [-1, 1]) b.box(10.8, 0.25, 0.9, M.tin, 0, TOP + 0.1, -20.6 + s * 3.0);
  // Puente de 5 V / 3.3 V: tres pines en fila y el cortocircuito sobre dos
  for (let i = 0; i < 3; i++) {
    b.box(2.5, 2.5, 2.5, M.blackPlastic, -6.35, TOP, -12.6 - i * P);
    b.box(0.64, 8, 0.64, M.gold, -6.35, TOP - 2, -12.6 - i * P);
  }
  // Componentes pequeños
  for (const [x, z, rot] of [[4.2, -3.6, 0], [-4.2, -3.6, 0], [4.6, -17.2, 1], [-4.2, -27.6, 1], [4.4, -27.4, 1]]) smd(b, x, TOP, z, rot ? 'c' : 'r', 0);
  b.box(3.2, 2.0, 2.2, mat('#2a2c30', { roughness: 0.6 }), 5.2, TOP, -29.0); // perla de ferrita
  b.build();
  // Cortocircuito del puente: pines 1-2 = 5 V (VCCIO = VCC del USB), 2-3 = 3.3 V (VCCIO = 3V3OUT)
  const pair = props.level === '3v3' ? [-12.6 - P, -12.6 - 2 * P] : [-12.6, -12.6 - P];
  const jb = new Builder();
  jumperCap(jb, [-6.35, pair[0]], [-6.35, pair[1]], TOP);
  jb.build();
  b.root.add(jb.root);
  // Rótulo del chip
  const label = labelPlane(9.2, 4.2, 'ftdi-chip', (g, w, d) => {
    g.fillStyle = '#1b1b1e';
    g.fillRect(0, 0, w, d);
    text(g, 'FTDI', w / 2, d / 2 - 0.9, { size: 1.2, color: '#9a9ca1', weight: 700 });
    text(g, 'FT232RL', w / 2, d / 2 + 0.25, { size: 1.0, color: '#9a9ca1', weight: 600 });
    text(g, 'AAJ-2209', w / 2, d / 2 + 1.35, { size: 0.7, color: '#7d7f84', weight: 500 });
  }, { transparent: false, s: 30 });
  label.rotation.x = -Math.PI / 2;
  label.position.set(0, TOP + 1.91, -20.6);
  b.root.add(label);
  // Enchufe Mini-USB y cable hacia el equipo
  const plug = new Builder();
  const plugZ = usbZ - 4.2 - 5.5;
  plug.box(6.9, 3.2, 10, M.metal, 0, TOP + 0.35, plugZ);
  plug.box(9.4, 6.0, 14, mat('#141416', { roughness: 0.6 }), 0, TOP - 1.0, plugZ - 11.5);
  cable(plug, [[0, TOP + 2, plugZ - 18], [0, TOP + 2, plugZ - 30], [0, 1.6, plugZ - 42], [0, 1.6, plugZ - 58]], 1.6, '#141416', 40);
  plug.build();
  b.root.add(plug.root);
  // LED de alimentación (fijo), TX y RX
  const pwr = smdLed(b.root, 3.4, TOP, -9.4, '#ff3324');
  const tx = smdLed(b.root, 3.4, TOP, -12.0, '#ff3324');
  const rx = smdLed(b.root, 3.4, TOP, -14.6, '#25e04f');
  const view = {
    root: b.root,
    update(v) {
      pwr.set(0.8);
      tx.set(v.tx ? 1 : 0);
      rx.set(v.led ? 1 : 0);
    },
    reset() {
      pwr.set(0);
      tx.set(0);
      rx.set(0);
    },
  };
  view.reset();
  return view;
}
